import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import TrustedSupplierIcon from '../assets/icons/trusted_icon.svg?react';
import TrustedSupplierBanner from '../assets/images/trusted_supplier_banner.png';
import { ContractPreview } from '../hooks/useContracts';
import { useGetAutoValidatedOrdersQuery } from '../queries/__generated__/getAutoValidatedOrders.generated';

interface ManageTrustedSupplierDialogProps {
  contract: ContractPreview;
  open: boolean;
  onClose: (confirm: boolean) => void;
}

export default function ManageTrustedSupplierDialog({ open, contract, onClose }: ManageTrustedSupplierDialogProps): React.JSX.Element {
  const { t } = useTranslation(['contracts', 'common']);
  const { data } = useGetAutoValidatedOrdersQuery({
    variables: {
      buyerCompanyId: contract.buyingCompanyId,
      supplierCompanyId: contract.supplyingCompanyId,
    },
  });

  const trustedPartnershipStats = useMemo(() => {
    if (!data?.order.length) {
      return;
    }

    return {
      orderCount: data.order.length,
      totalAmount: data.order.reduce((acc, prev) => acc + prev.total_price_to_pay_excl_tax!, 0),
    };
  }, [data]);

  return (
    <Dialog open={open} onClose={() => onClose(false)}>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexDirection: 'row',
        }}
      >
        <TrustedSupplierIcon width={24} height={24} />
        {t('contracts:trusted_supplier.dialog_title')}
      </DialogTitle>
      <DialogContent>
        <Box
          component="img"
          src={TrustedSupplierBanner}
          sx={{
            display: 'block',
            mx: 'auto',
            width: '100%',
            aspectRatio: '3/1',
          }}
        />
        <Box sx={{ mt: 2 }}>
          {trustedPartnershipStats && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="titleSmall">{t('contracts:trusted_supplier.trusted_partnership_stats', { count: trustedPartnershipStats.orderCount, totalAmount: trustedPartnershipStats.totalAmount })}</Typography>
              <Typography variant="bodyMedium">{t('contracts:trusted_supplier.trusted_description')}</Typography>
              <Divider sx={{ mt: 1, mb: 2 }} />
            </Box>
          )}
          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
          >
            <Typography variant="titleSmall">{t('common:no_longer_interested')}</Typography>
            <Typography variant="bodyMedium">{t('contracts:trusted_supplier.no_trusted_description')}</Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          flexDirection: {
            xs: 'column-reverse',
            sm: 'row',
          },
        }}
      >
        <Button
          onClick={() => onClose(true)}
          sx={{
            width: {
              xs: '100%',
              sm: 'fit-content',
            },
          }}
        >
          {t('contracts:trusted_supplier.deactivate_partnership')}
        </Button>
        <Button
          sx={{
            width: {
              xs: '100%',
              sm: 'fit-content',
            },
          }}
          variant="contained"
          onClick={() => onClose(false)}
        >
          {t('common:understood')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
